import { useCallback, useEffect, useRef, useState } from 'react';
import { AbortedError } from './AbortedError';

export type UseAsyncOptions<T> = {
  onError?: (error: Error) => void;
  defaultValue?: T;
};

export type AsyncFunction<V> = (...args: never[]) => PromiseLike<V>;
export type AsyncReturnType<V> = V extends AsyncFunction<infer U> ? U : V;

export const useAsync = <
  // eslint-disable-next-line
  T extends (...args: any[]) => Promise<any>,
  V = AsyncReturnType<T>,
>(
  serviceImplementation: T,
  options?: UseAsyncOptions<Awaited<ReturnType<T>>>
) => {
  const [state, setState] = useState({
    isLoading: false,
    isCompleted: false,
    isRequested: false
  });
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<V | null>(options?.defaultValue ?? null);

  // Tracks the AbortController of the currently in-flight call, so we can
  // cancel it (either explicitly via `cancel()`, when a new `run` is
  // triggered, or on unmount) and ignore its result/error afterwards.
  const controllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const cancel = useCallback(() => {
    console.log('abort');
    controllerRef.current?.abort();
  }, []);

  const run = useCallback(
    async (...params: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
      // Cancel any previous in-flight call before starting a new one.
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setState(prev => ({
        ...prev,
        isLoading: true,
        isRequested: true
      }));
      setError(null);

      const isStale = () => controller.signal.aborted || !isMountedRef.current;

      try {
        const response = await serviceImplementation(...params);

        if (isStale()) {
          throw new AbortedError();
        }

        setData(response);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isRequested: true,
          isCompleted: true
        }));

        return response;
      } catch (err) {
        if (isStale()) {
          throw err instanceof AbortedError ? err : new AbortedError();
        }

        setError(err as Error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isCompleted: true
        }));

        if (options?.onError) {
          options.onError(err as Error);
        }
        throw err;
      }
    },
    [serviceImplementation, options]
  );

  const mutate = (mutation: (currentState: V) => V) => {
    setData(currentState => {
      return mutation(currentState as V);
    });
  };

  const set = useCallback((value: V | null) => {
    setData(value);
  }, []);

  return {
    isLoading: state.isLoading,
    run,
    data,
    error,
    isRequested: state.isRequested,
    isCompleted: state.isCompleted,
    set,
    cancel,
    mutate,
  };
};